<!--
  Aspedia VueJS Helper Component
  *=====
  Import Name:  tree-view
  Name:         TreeView
  Version:      1
  Date:         2018-03-13
  Author:       Christian Dale <cdale@aspedia.net>

  Description:      Generic Tree component for creating nested tree/data structures.
  Default Data:     N/A
  Component IMG:    N/A
  Ancestor:         N/A
  Available To:     N/A
  Builder Visible:  False
  Version History:
    1    2018-03-13    Christian Dale <cdale@aspedia.net>   Initial revision of the Tree Component.
  *=====
-->

<template>
  <component
    :is="currentParentTag()"
    v-bind="currentParentAttributes()"
  >
    <template v-for="(item, index) in currentModel()">
      <!-- The with-root functional component causes undefined behaviour when :key is used.-->
      <!-- eslint-disable-next-line vue/valid-v-for -->
      <with-root :show="renderCurrentChildTag(item)">
        <component
          :is="currentChildTag(item)"
          v-bind="currentChildAttributes(item)"
        >
          <slot
            :item="item"
            :index="index"
            :depth="depth"
          />
          <tree-view
            v-if="hasChildren(item)"
            :model="item"
            :index="index"
            :depth="depth + 1"
            :parent-tag="parentTag"
            :parent-attributes="parentAttributes"
            :render-child-tag="renderChildTag"
            :child-tag="childTag"
            :child-attributes="childAttributes"
            :child-without-children-attributes="childWithoutChildrenAttributes"
            :child-with-children-attributes="childWithChildrenAttributes"
          >
            <template slot-scope="_">
              <slot
                :item="_.item"
                :index="_.index"
                :depth="_.depth"
              />
            </template>
          </tree-view>
        </component>
      </with-root>
    </template>
  </component>
</template>

<script>
import {deepmerge} from 'app/utils'
import withRoot from './with-root'

export default {
  name: 'tree-view',
  components: {withRoot},
  props: {
    // The data model.
    model: {
      type: [Array, Object],
      required: true
    },

    // The depth of this component.
    depth: {
      type: Number,
      required: false,
      default: 0
    },

    // Tags that are used for depth 0.
    rootTag: {
      type: String,
      required: false,
      default: 'ul'
    },
    rootAttributes: {
      type: Array,
      required: false,
      default: function() { return [] }
    },

    // Root child tags (child when depth is 0).
    renderRootChildTag: {
      type: [Boolean, Function],
      required: false,
      default: true
    },
    rootChildTag: {
      type: [String, Function],
      required: false,
      default: 'li'
    },
    rootChildAttributes: {
      type: [Array, Function],
      required: false,
      default: function() { return [] }
    },
    rootChildWithoutChildrenAttributes: {
      type: [Array, Function],
      required: false,
      default: function() { return [] }
    },
    rootChildWithChildrenAttributes: {
      type: [Array, Function],
      required: false,
      default: function() { return [] }
    },

    // Parent tags when depth is not 0.
    parentTag: {
      type: [String, Function],
      required: false,
      default: 'ul'
    },
    parentAttributes: {
      type: [Array, Function],
      required: false,
      default: function() { return [] }
    },

    // Child tags (child when depth is not 0).
    renderChildTag: {
      type: [Boolean, Function],
      required: false,
      default: true
    },
    childTag: {
      type: [String, Function],
      required: false,
      default: 'li'
    },
    childAttributes: {
      type: [Array, Function],
      required: false,
      default: function() { return [] }
    },
    childWithoutChildrenAttributes: {
      type: [Array, Function],
      required: false,
      default: function() { return [] }
    },
    childWithChildrenAttributes: {
      type: [Array, Function],
      required: false,
      default: function() { return [] }
    }
  },
  methods: {
    isRoot: function() {
      return this.depth === 0
    },
    hasChildren: function(item) {
      return item.children && item.children.length > 0
    },
    currentModel: function() {
      return this.depth === 0 ? this.model : this.model.children
    },
    currentParentTag: function() {
      let tag = this.isRoot() ? this.rootTag : this.parentTag
      return (typeof tag === 'string') ? tag : tag(this.model, this.depth)
    },
    currentChildTag: function(item) {
      let tag = this.isRoot() ? this.rootChildTag : this.childTag
      return (typeof tag === 'string') ? tag : tag(item, this.model, this.depth)
    },
    renderCurrentChildTag: function(item) {
      let render = this.isRoot() ? this.renderRootChildTag : this.renderChildTag
      return (typeof render === 'boolean') ? render : render(item, this.model, this.depth)
    },
    currentParentAttributes: function() {
      let attributes = this.isRoot() ? this.rootAttributes : this.parentAttributes
      return (Array.isArray(attributes)) ? attributes : attributes(this.model, this.depth)
    },
    currentChildAttributes: function(item) {
      let hasChildren = this.hasChildren(item)
      let attributes = this.childAttributes
      let extraAttributes = hasChildren ? this.childWithChildrenAttributes : this.childWithoutChildrenAttributes

      if (this.isRoot()) {
        attributes = this.rootChildAttributes
        extraAttributes = hasChildren ? this.rootChildWithChildrenAttributes : this.rootChildWithoutChildrenAttributes
      }

      if (typeof attributes === 'function') {
        attributes = attributes(item, this.model, this.depth, hasChildren)
      }
      if (typeof extraAttributes === 'function') {
        extraAttributes = extraAttributes(item, this.model, this.depth, hasChildren)
      }

      return deepmerge(attributes, extraAttributes)
    }
  }
}
</script>
